import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '@/services/api-client';
import { FormError } from '@/components/ui/form-error';
import { handleApiError } from '@/lib/handle-api-error';

const forgotPasswordSchema = z.object({
  email: z.string().email("Email inválido"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      await apiClient.forgotPassword(data);
      setEmail(data.email);
      setIsSubmitted(true);
    } catch (error) {
      handleApiError(error, { form });
    }
  };

  if (isSubmitted) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Verifique seu e-mail</CardTitle>
          <CardDescription>
            Enviamos um e-mail para {email} com as instruções para redefinir sua senha.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Link to="/login" className="w-full">
            <Button className="w-full">Fazer login</Button>
          </Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Esqueci minha senha</CardTitle>
        <CardDescription>
          Digite seu e-mail para receber o código e atualizar sua senha
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleForgotPassword)}>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="Digite seu e-mail" 
                {...register("email")} 
              />
              <FormError message={errors.email?.message} className="mt-1" />
            </div>
          </div>
          <Button className="w-full mt-4" type="submit">Enviar código</Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Link to="/login">
          <Button variant="outline">Cancelar</Button>
        </Link>
      </CardFooter>
    </Card>
  );
};